import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { oidcAuthApiRef } from './oidcAuthApi';

/**
 * Native OIDC sign-in page for Backstage.
 *
 * Extension id: `sign-in-page:app` (intentionally the same as plugin-app's
 * DefaultSignInPage). When a module's extension shares an id with a plugin's,
 * the frontend-app resolver treats it as a replacement rather than a sibling
 * (see `resolveAppNodeSpecs` in `@backstage/frontend-app-api` — the same-id
 * branch mutates the existing entry with the module's extension). A named
 * sibling plus a config-disable of the default does NOT reliably win the
 * `signInPage` singleton input.
 *
 * `auto` triggers an instant popup on mount: the popup loads
 * `/api/auth/oidc/start`, which the Backstage backend redirects to Authentik
 * (`https://auth0.mereka.dev/application/o/authorize/?client_id=backstage&...`).
 * If the user already has an Authentik session, the popup completes
 * silently — no click, no visible login UI. If not, the user signs in via
 * Authentik in the popup, the backend posts the result back to the parent
 * window via `/api/auth/oidc/handler/frame`, and the SignInPage resolves
 * with a real Backstage identity.
 *
 * This replaces the previous `ProxiedSignInPage` flow that depended on
 * ingress-nginx forward-auth + Authentik proxy provider headers. Forward-auth
 * was removed because the dev embedded outpost has multiple proxy providers
 * sharing `cookie_domain: mereka.dev`, which causes the outpost to randomly
 * pick a provider per request (Go map iteration order) and produce infinite
 * redirect loops. See bbi-infrastructure PR for the full context.
 */
export const oidcSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      const { SignInPage } = await import('@backstage/core-components');
      return props => (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'oidc',
            title: 'Mereka SSO',
            message: 'Sign in via Mereka',
            apiRef: oidcAuthApiRef,
          }}
        />
      );
    },
  },
});
